import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Testimonial } from '../models/testimonials.types';
import { testimonials } from '../data/testimonials.data';

@Injectable({
  providedIn: 'root'
})
export class TestimonialsService {
  private testimonials: Testimonial[] = testimonials;

  constructor() {}

  /**
   * Helper method to fetch query results
   */
  private getFetchResults(fetchFn: () => Testimonial[]): Observable<Testimonial[]> {
    try {
      return of(fetchFn());
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      return of([]);
    }
  }

  /**
   * Get testimonials for landing page
   */
  getLandingPageTestimonials(limit: number = 3): Observable<Testimonial[]> {
    return this.getFetchResults(() => {
      try {
        return this.testimonials
          .filter(t => t.tags && t.tags.landingPage === true)
          .slice(0, limit);
      } catch (error) {
        console.error('Error filtering landing page testimonials:', error);
        return [];
      }
    });
  }

  /**
   * Get testimonials for a specific product
   */
  getProductTestimonials(product: string, limit: number = 3): Observable<Testimonial[]> {
    return this.getFetchResults(() => {
      try {
        return this.testimonials
          .filter(t => t.tags && t.tags.products && t.tags.products.includes(product))
          .slice(0, limit);
      } catch (error) {
        console.error('Error filtering product testimonials:', error);
        return [];
      }
    });
  }

  /**
   * Get testimonials for a specific industry
   */
  getIndustryTestimonials(industry: string, limit: number = 3): Observable<Testimonial[]> {
    return this.getFetchResults(() => {
      try {
        return this.testimonials
          .filter(t => t.tags && t.tags.industries && t.tags.industries.includes(industry))
          .slice(0, limit);
      } catch (error) {
        console.error('Error filtering industry testimonials:', error);
        return [];
      }
    });
  }

  /**
   * Get testimonials for a specific business function
   */
  getFunctionTestimonials(functionName: string, limit: number = 3): Observable<Testimonial[]> {
    return this.getFetchResults(() => {
      try {
        return this.testimonials
          .filter(t => t.tags && t.tags.functions && t.tags.functions.includes(functionName))
          .slice(0, limit);
      } catch (error) {
        console.error('Error filtering function testimonials:', error);
        return [];
      }
    });
  }

  /**
   * Filter testimonials by multiple criteria
   */
  filterTestimonials(options: {
    product?: string,
    industry?: string,
    function?: string,
    limit?: number
  }): Observable<Testimonial[]> {
    const { product, industry, function: businessFunction, limit = 3 } = options;
    
    return this.getFetchResults(() => {
      try {
        let results = this.testimonials;
        
        if (product) {
          results = results.filter(t => t.tags && t.tags.products && t.tags.products.includes(product));
        }
        
        if (industry) {
          results = results.filter(t => t.tags && t.tags.industries && t.tags.industries.includes(industry));
        }
        
        if (businessFunction) {
          results = results.filter(t => t.tags && t.tags.functions && t.tags.functions.includes(businessFunction));
        }
        
        return results.slice(0, limit);
      } catch (error) {
        console.error('Error filtering testimonials:', error);
        return [];
      }
    });
  }
}